import Microgame from '../engine/Microgame.js';

export default class TrafficGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Trafic !`, en: `Traffic!`, es: `¡Tráfico!` },
            controls: { fr: `Appuie sur les voitures pour les faire accélérer`, en: `Tap cars to make them speed up`, es: `Toca los coches para que aceleren` },
            objective: { fr: `Évite que les voitures ne se rentrent dedans`, en: `Prevent the cars from colliding`, es: `Evita que los coches choquen` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><rect x="6" y="8" width="12" height="8" rx="2"/><circle cx="9" cy="16" r="2"/><circle cx="15" cy="16" r="2"/><path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="m14 12-4-4"/></svg>`,
            actions: [
                { type: 'wait', duration: 280 },
                { type: 'tap', x: 50, y: 88, duration: 130 },
                { type: 'wait', duration: 420 },
                { type: 'tap', x: 50, y: 52, duration: 130 },
                { type: 'wait', duration: 420 },
                { type: 'tap', x: 50, y: 20, duration: 130 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.player = null;
        this.playerY = 90; // Start at bottom
        this.lane1Cars = [];
        this.lane2Cars = [];
        this.step = 0; // 0 = start, 1 = middle, 2 = end
        this.isMoving = false;
        this.moveSpeed = 100; // % per sec (vertical speed)

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;

        // Base 4 seconds at x1, 1.5 seconds at x4
        // Formula: maxTime = duration / speedMult. 
        // We set duration = 4000 * (speedMult / adjustedSpeed) so maxTime = 4000 / adjustedSpeed.
        // adjustedSpeed(1) = 1 (maxTime = 4000). adjustedSpeed(4) = 4000/1500 = 2.666
        const adjustedSpeed = 1 + (speedMult - 1) * (5 / 9);
        this.duration = 4000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "CROSS!";

        // Road
        const road = document.createElement('div');
        road.style.position = 'absolute';
        road.style.top = '20%';
        road.style.bottom = '20%';
        road.style.width = '100%';
        road.style.background = 'rgba(0,0,0,0.3)';
        road.style.borderTop = `2px solid ${this.secondaryColor}`;
        road.style.borderBottom = `2px solid ${this.secondaryColor}`;
        road.style.boxShadow = `0 0 20px ${this.secondaryColor}44`;
        this.container.appendChild(road);

        // Goal Zone (Top)
        const goal = document.createElement('div');
        goal.style.position = 'absolute';
        goal.style.top = '5%';
        goal.style.left = '10%';
        goal.style.right = '10%';
        goal.style.height = '10%';
        goal.style.borderRadius = '10px';
        goal.style.border = `2px dashed ${this.primaryColor}`;
        goal.style.display = 'flex';
        goal.style.alignItems = 'center';
        goal.style.justifyContent = 'center';
        goal.style.background = `${this.primaryColor}11`;
        this.container.appendChild(goal);

        const goalText = document.createElement('div');
        goalText.textContent = "FINISH";
        goalText.style.color = this.primaryColor;
        goalText.style.fontWeight = '900';
        goalText.style.fontSize = '18px';
        goalText.style.textShadow = `0 0 10px ${this.primaryColor}`;
        goal.appendChild(goalText);

        // Player
        this.player = document.createElement('div');
        this.player.style.position = 'absolute';
        this.player.style.width = '24px';
        this.player.style.height = '24px';
        this.player.style.background = this.primaryColor;
        this.player.style.borderRadius = '2px';
        this.player.style.border = `2px solid ${this.secondaryColor}`;
        this.player.style.left = '50%';
        this.player.style.top = `${this.playerY}%`;
        this.player.style.transform = `translate(-50%, -50%) translateZ(0)`;
        this.player.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.player.style.zIndex = '10';
        this.container.appendChild(this.player);

        // Cars
        this.spawnCars();

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    spawnCars() {
        // Significantly larger, scattered safe gaps
        const gap1 = 20 + Math.random() * 60;
        const gap2 = 20 + Math.random() * 60;

        // Lane 1: Y=65%
        this.createCarRow(65, 1, gap1);
        // Lane 2: Y=35%
        this.createCarRow(35, -1, gap2);
    }

    createCarRow(y, dir, gapPos) {
        const carCount = 3;
        const spacing = 150 / carCount;

        for (let i = 0; i < carCount; i++) {
            const pos = (i * spacing) - 25;
            if (Math.abs(pos - gapPos) < 30) continue;

            const car = document.createElement('div');
            car.style.position = 'absolute';
            car.style.width = '70px';
            car.style.height = '30px';
            car.style.background = `linear-gradient(${dir > 0 ? '90deg' : '-90deg'}, ${this.secondaryColor}, transparent)`;
            car.style.border = `1px solid ${this.secondaryColor}`;
            car.style.borderRadius = '4px';
            car.style.zIndex = '5';
            car.style.boxShadow = `0 0 15px ${this.secondaryColor}88`;
            car.style.left = `${pos}%`;
            car.style.top = `${y}%`;
            car.style.transform = 'translate(-50%, -50%) translateZ(0)';

            // Neon light strip on car
            const strip = document.createElement('div');
            strip.style.position = 'absolute';
            strip.style.top = '50%';
            strip.style.width = '60%';
            strip.style.height = '2px';
            strip.style.background = this.primaryColor;
            strip.style.boxShadow = `0 0 8px ${this.primaryColor}`;
            if (dir === 1) strip.style.right = '0'; else strip.style.left = '0';
            strip.style.transform = 'translateY(-50%)';
            car.appendChild(strip);

            this.container.appendChild(car);

            const speedMult = this.engine.speedMultiplier || 1.0;
            const speed = (30 + Math.random() * 15) * speedMult * dir;

            if (y === 65) this.lane1Cars.push({ el: car, x: pos, y: y, speed: speed });
            else this.lane2Cars.push({ el: car, x: pos, y: y, speed: speed });
        }
    }

    update(dt) {
        if (!this.active) return;

        const timeFactor = dt / 1000;
        this.moveCars(this.lane1Cars, timeFactor);
        this.moveCars(this.lane2Cars, timeFactor);

        if (!this.won && !this._isAnimating) {
            this.checkCollision(this.lane1Cars);
            this.checkCollision(this.lane2Cars);
        }

        // Player Movement (Smooth transition)
        if (this.isMoving) {
            let targetY = 90;
            if (this.step === 1) targetY = 50;
            else if (this.step === 2) targetY = 10;

            const diff = targetY - this.playerY;
            if (Math.abs(diff) > 2) {
                this.playerY += (diff > 0 ? 1 : -1) * this.moveSpeed * timeFactor;
                this.player.style.top = `${this.playerY}%`;
            } else {
                this.playerY = targetY;
                this.isMoving = false;
                this.player.style.top = `${this.playerY}%`;

                if (this.step === 2) {
                    this.win();
                }
            }
        }

        // Freeze engine timer during explosion animation
        if (this._isAnimating && this.engine.timer !== Infinity) {
            this.engine.timer += dt;
        }
    }

    moveCars(cars, timeFactor) {
        for (let car of cars) {
            car.x += car.speed * timeFactor;
            if (car.x > 140) car.x = -40;
            else if (car.x < -40) car.x = 140;
            car.el.style.left = `${car.x}%`;
        }
    }

    checkCollision(cars) {
        // Pixel-accurate percentage math
        const pX = 50;
        const pY = this.playerY;
        const pRadius = 2.5; // Very tight

        for (const car of cars) {
            const cX = car.x;
            const cY = car.y;
            const cW = 8.5; // (70px/400px * 100) / 2
            const cH = 2.5; // (30px/600px * 100) / 2

            // AABB Collision (Percentage based)
            if (pX - pRadius < cX + cW &&
                pX + pRadius > cX - cW &&
                pY - pRadius < cY + cH &&
                pY + pRadius > cY - cH) {
                
                this._isAnimating = true;

                // Visual Collision
                this.player.style.background = this.secondaryColor;
                this.player.style.top = `${this.playerY}%`;
                this.player.style.transform = `translate(-50%, -50%) scale(2) rotate(45deg) translateZ(0)`;
                this.player.style.boxShadow = `0 0 30px ${this.secondaryColor}`;
                this.isMoving = false;

                // Simple explosion
                for (let i = 0; i < 12; i++) {
                    const p = document.createElement('div');
                    p.style.position = 'absolute';
                    p.style.width = '8px';
                    p.style.height = '8px';
                    p.style.background = i % 2 === 0 ? this.primaryColor : this.secondaryColor;
                    p.style.left = '50%';
                    p.style.top = `${this.playerY}%`;
                    p.style.zIndex = '15';
                    p.style.transform = `translate(-50%, -50%)`;
                    p.style.transition = 'all 0.8s cubic-bezier(0.1, 0.8, 0.2, 1)';
                    this.container.appendChild(p);
                    
                    const angle = Math.random() * Math.PI * 2;
                    const dist = 60 + Math.random() * 100;
                    
                    setTimeout(() => {
                        p.style.opacity = '0';
                        p.style.transform = `translate(-50%, -50%) translate(${Math.cos(angle) * dist}px, ${Math.sin(angle) * dist}px) scale(0)`;
                    }, 50);
                    setTimeout(() => p.remove(), 900);
                }

                setTimeout(() => {
                    if (this.active) this.lose();
                }, 1000);

                return;
            }
        }
    }

    onDown() {
        if (!this.active || this.won || this.isMoving || this.step >= 2) return;

        this.step++;
        this.isMoving = true;
        this.moveSpeed = 150; // Balanced speed
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
